import { useEffect, useRef, useState } from 'react';
import { Maximize, Pause, Play, Volume2, VolumeX } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLang } from '../i18n';

const mediaUrl = (value) => value?.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${value}` : value;
const portraitMedia = '(max-width: 640px) and (orientation: portrait)';
const isPortraitMobile = () => Boolean(window.matchMedia?.(portraitMedia).matches);

export const BrandFilm = ({ film }) => {
  const { t } = useLang();
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [portrait, setPortrait] = useState(isPortraitMobile);

  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const query = window.matchMedia(portraitMedia);
    const update = () => setPortrait(query.matches);
    update();
    query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, []);

  const videoUrl = portrait && film.social_video_url ? film.social_video_url : film.video_url;
  const togglePlay = async () => {
    const video = videoRef.current;
    if (!video) return;
    if (!video.paused) { video.pause(); setPlaying(false); return; }
    try { await video.play(); setPlaying(true); } catch { setPlaying(false); }
  };
  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !muted;
    setMuted((value) => !value);
  };
  const fullscreen = () => videoRef.current?.requestFullscreen?.();
  const ctaTo = film.cta_target === 'plan' ? '/plan-my-trip' : film.cta_target === 'explore' ? '/experiences' : '';

  return (
    <section className="bg-eco-cream py-20 md:py-24" data-testid="brand-film">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <p className="eco-eyebrow">{t('brandFilm.kicker')}</p>
          <h2 className="eco-section-title mt-3">{film.title}</h2>
          {film.subtitle && <p className="mt-3 font-serif text-xl italic text-eco-gold">{film.subtitle}</p>}
          {film.caption && <p className="mx-auto mt-4 max-w-2xl leading-relaxed text-eco-ink/65">{film.caption}</p>}
        </div>
        <div className="relative mx-auto mt-9 aspect-video w-full max-w-6xl overflow-hidden rounded-2xl bg-eco-deep shadow-2xl" data-testid="brand-film-player">
          <video
            key={videoUrl}
            ref={videoRef}
            src={mediaUrl(videoUrl)}
            poster={mediaUrl(film.poster_url) || undefined}
            preload="metadata"
            muted={muted}
            playsInline
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onEnded={() => setPlaying(false)}
            aria-label={film.poster_alt || film.title}
            className="h-full w-full object-contain"
          />
          <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-gradient-to-t from-black/80 via-black/30 to-transparent p-4 pt-20 sm:p-6 sm:pt-24">
            <button type="button" onClick={togglePlay} aria-label={playing ? t('moments.pause') : t('brandFilm.watch')} aria-pressed={playing} className="inline-flex min-h-11 items-center gap-2 rounded-full bg-white px-5 text-sm font-semibold text-eco-deep focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-eco-gold">
              {playing ? <Pause size={18} /> : <Play size={18} />} {playing ? t('moments.pause') : t('brandFilm.watch')}
            </button>
            <div className="flex gap-2">
              <button type="button" onClick={toggleMute} aria-label={muted ? t('moments.unmute') : t('moments.mute')} aria-pressed={!muted} className="grid h-11 w-11 place-items-center rounded-full bg-black/55 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-eco-gold">{muted ? <VolumeX size={18} /> : <Volume2 size={18} />}</button>
              <button type="button" onClick={fullscreen} aria-label={t('brandFilm.fullscreen')} className="grid h-11 w-11 place-items-center rounded-full bg-black/55 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-eco-gold"><Maximize size={18} /></button>
            </div>
          </div>
        </div>
        {ctaTo && <div className="mt-7 text-center"><Link to={ctaTo} className="eco-btn-secondary">{film.cta_label || (film.cta_target === 'plan' ? t('brandFilm.plan') : t('brandFilm.explore'))}</Link></div>}
      </div>
    </section>
  );
};
